import { z } from 'zod/v4';

/**
 * Public-facing representation of a supplier's amfori link — the three
 * supplier-side identifiers imported during a brand's amfori sync and
 * persisted as self-reported answers on the supplier company.
 *
 * `linked` is the visibility bit: `true` when at least one of the three
 * identifiers is present. An all-null triple for an authorized caller is a
 * valid resource with `linked: false` (NOT a 404).
 *
 * `provenance` folds every present row's `_meta.source`: `'MANUAL'` when any
 * present row was set by a human, else `'SYNC'`.
 */
export const AmforiSupplierLinkResourceSchema = z
  .object({
    companyId: z.string(),
    businessPartnerId: z.string().nullable(),
    siteId: z.string().nullable(),
    lastSyncedAt: z.string().nullable(),
    linked: z.boolean(),
    provenance: z.enum(['SYNC', 'MANUAL']),
    // Who/when of the last MANUAL edit to the site id (null otherwise), from the
    // link's history — drives the "manually linked by {user} on {date}" line on
    // the supplier panel.
    siteIdUpdatedByName: z.string().nullable(),
    siteIdUpdatedAt: z.string().nullable(),
    // `true` when another company set the site id and still owns it, so the
    // viewing brand may not change or unlink it (DELTA-2764). Never names it.
    lockedByAnotherCompany: z.boolean(),
  })
  .strict();

export type AmforiSupplierLinkResource = z.infer<
  typeof AmforiSupplierLinkResourceSchema
>;
